import {
  useEffect, useId, useMemo, useState, type MouseEvent,
} from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTranslate } from 'ra-core';
import { useFormContext } from 'react-hook-form';
import {
  buildOsFilterPayload,
  ClientConfigOsFilter,
  formatOsFilterChipLabel,
  parseOsFilterFormFields,
} from '../../lib/clientConfigHelpers';

type DraftState = {
  enabled: boolean;
  osType: 'ios' | 'android' | '';
  osVersionRange: string;
};

const toDraft = (filter?: ClientConfigOsFilter): DraftState => {

  const parsed = parseOsFilterFormFields(filter);
  return {
    enabled: parsed.osFilterEnabled,
    osType: parsed.osFilterOsType,
    osVersionRange: parsed.osFilterOsVersionRange,
  };

};

export const ClientConfigOsFilterControl = (props: {
  value?: ClientConfigOsFilter;
  onChange: (filter: ClientConfigOsFilter | undefined) => void;
  helpTextKey?: string;
}) => {

  const { value, onChange, helpTextKey = 'clientConfig.osFilter.targetFilterHelp' } = props;
  const translate = useTranslate();
  const osTypeLabelId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<DraftState>(() => toDraft(value));

  const chipLabels = useMemo(() => ({
    allDevices: translate('clientConfig.osFilter.allDevices', { _: 'All devices' }),
    ios: translate('clientConfig.osFilter.ios', { _: 'iOS' }),
    android: translate('clientConfig.osFilter.android', { _: 'Android' }),
  }), [translate]);

  const chipLabel = formatOsFilterChipLabel(value, chipLabels);
  const open = Boolean(anchorEl);

  const openPopover = (event: MouseEvent<HTMLElement>) => {

    setDraft(toDraft(value));
    setAnchorEl(event.currentTarget);

  };

  const closePopover = () => {

    setAnchorEl(null);

  };

  const applyDraft = () => {

    onChange(buildOsFilterPayload(draft.enabled, draft.osType, draft.osVersionRange));
    closePopover();

  };

  const clearFilter = () => {

    onChange(undefined);
    setDraft({ enabled: false, osType: '', osVersionRange: '' });
    closePopover();

  };

  return (
    <>
      <Chip
        size="small"
        icon={<FilterAltOutlinedIcon fontSize="small" />}
        label={chipLabel}
        onClick={openPopover}
        variant={value?.osType ? 'filled' : 'outlined'}
        sx={{ height: 28, cursor: 'pointer' }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{
          p: 2, width: 280, display: 'flex', flexDirection: 'column', gap: 1.5,
        }}>
          <Typography variant="subtitle2">
            {translate('clientConfig.osFilter.enabled', { _: 'Limit to specific OS' })}
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel id={osTypeLabelId}>
              {translate('clientConfig.osFilter.osType', { _: 'Operating system' })}
            </InputLabel>
            <Select
              labelId={osTypeLabelId}
              label={translate('clientConfig.osFilter.osType', { _: 'Operating system' })}
              value={draft.enabled ? draft.osType : ''}
              onChange={(event) => {

                const nextOsType = event.target.value as 'ios' | 'android' | '';
                setDraft({
                  enabled: Boolean(nextOsType),
                  osType: nextOsType,
                  osVersionRange: draft.osVersionRange,
                });

              }}
            >
              <MenuItem value="">
                {chipLabels.allDevices}
              </MenuItem>
              <MenuItem value="ios">{chipLabels.ios}</MenuItem>
              <MenuItem value="android">{chipLabels.android}</MenuItem>
            </Select>
          </FormControl>
          {draft.enabled && (
            <TextField
              size="small"
              fullWidth
              label={translate('clientConfig.osFilter.osVersionRange', { _: 'OS version range' })}
              helperText={translate('clientConfig.osFilter.osVersionRangeHelp', { _: 'Optional semver, e.g. >=12.0.0' })}
              value={draft.osVersionRange}
              onChange={(event) => setDraft((previous) => ({
                ...previous,
                osVersionRange: event.target.value,
              }))}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {translate(helpTextKey, { _: 'Applies to this configuration block only.' })}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button size="small" onClick={clearFilter}>
              {translate('ra.action.clear', { _: 'Clear' })}
            </Button>
            <Button size="small" variant="contained" onClick={applyDraft} disabled={draft.enabled && !draft.osType}>
              {translate('ra.action.confirm', { _: 'Confirm' })}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );

};

export const ClientConfigOsFilterFormControl = (props: {
  helpTextKey?: string;
}) => {

  const { helpTextKey } = props;
  const { watch, setValue, register } = useFormContext();
  const enabled = watch('osFilterEnabled');
  const osType = watch('osFilterOsType');
  const osVersionRange = watch('osFilterOsVersionRange');
  const filter = buildOsFilterPayload(enabled, osType, osVersionRange);

  useEffect(() => {

    register('osFilterEnabled');
    register('osFilterOsType');
    register('osFilterOsVersionRange');

  }, [register]);

  return (
    <ClientConfigOsFilterControl
      value={filter}
      helpTextKey={helpTextKey}
      onChange={(nextFilter) => {

        const parsed = parseOsFilterFormFields(nextFilter);
        setValue('osFilterEnabled', parsed.osFilterEnabled, { shouldDirty: true });
        setValue('osFilterOsType', parsed.osFilterOsType, { shouldDirty: true });
        setValue('osFilterOsVersionRange', parsed.osFilterOsVersionRange, { shouldDirty: true });

      }}
    />
  );

};

export const ClientConfigOsFilterChip = (props: {
  filter?: ClientConfigOsFilter;
  showWhenUnset?: boolean;
}) => {

  const { filter, showWhenUnset = false } = props;
  const translate = useTranslate();

  if (!filter?.osType && !showWhenUnset) {

    return null;

  }

  const label = formatOsFilterChipLabel(filter, {
    allDevices: translate('clientConfig.osFilter.allDevices', { _: 'All devices' }),
    ios: translate('clientConfig.osFilter.ios', { _: 'iOS' }),
    android: translate('clientConfig.osFilter.android', { _: 'Android' }),
  });

  return (
    <Chip
      size="small"
      icon={<FilterAltOutlinedIcon fontSize="small" />}
      label={label}
      sx={{ height: 24 }}
    />
  );

};
